/* eslint-disable no-nested-ternary */
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Card, Grid, MenuItem, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import FormProvider, { RHFSelect, RHFTextField } from '@/commons/hook-form';
import { useAuthContext } from '@/hooks/useAuthContext';
import { findUsersByBranch } from '@/services';
import { isOrderCustom, translates } from '@/utils';
import { Roles } from '@/utils/roles';
import { useSearchParams } from 'react-router-dom';
import { LoadingButton } from '@mui/lab';
import { useCompanyCategory } from '@/hooks/useCompanyCategory';
import { Order } from '../../types';
import { ProductItemsGridCustomOrder } from '../order-detail/product-items-custom';
import { ProductItemsGridOrder } from '../order-detail/product-items-grid';
import { ProductItemsListOrder } from '../order-detail/products-items-list';
import { defaultValuesProductForm } from './initial-values';
import { orderSchema, orderSchemaOptional } from './validation';

type FormValuesProps = Partial<Order>;
type Props = {
  isEdit?: boolean;
  isGrid?: boolean;
  currentProduct?: Order;
  onSave: (values: any) => void;
  isLoading: boolean;
};

export default function OrderNewEditForm({
  isEdit,
  currentProduct,
  onSave,
  isLoading,
  isGrid,
}: Props) {
  const { user } = useAuthContext();
  const [searchParams] = useSearchParams();
  const { isFoodCompany } = useCompanyCategory();

  const isCustomOrder = isOrderCustom(searchParams.get('type') ?? '');
  const generateRole = (role: number) => {
    if (role === Roles.ADMIN || role === Roles.SUPER_ADMIN || role === Roles.ATM) {
      return true;
    }
    return false;
  };
  const isAvailableVendor = generateRole(user?.role?.id ?? 1);
  const { data: dataUsers } = useQuery({
    queryKey: ['users'],
    queryFn: findUsersByBranch,
    select: (dataTemp: any) =>
      dataTemp.filter(
        (item: { status: any; rol: { id: Roles } }) =>
          item.status &&
          !!item.rol &&
          (item.rol?.id === Roles.WAITRESS ||
            item.rol?.id === Roles.ATM ||
            item.rol.id === Roles.ADMIN ||
            item.rol.id === Roles.SUPER_ADMIN)
      ),
  });

  const defaultValues = useMemo(
    () => defaultValuesProductForm(currentProduct),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentProduct, isAvailableVendor]
  );

  const methods = useForm<FormValuesProps>({
    resolver: yupResolver(isAvailableVendor ? orderSchema : orderSchemaOptional),
    defaultValues,
  } as any);

  const {
    reset,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  useEffect(() => {
    if (currentProduct && isEdit) {
      reset(defaultValues as unknown as Partial<Order>);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentProduct, isEdit]);

  const onSubmit = async (data: FormValuesProps) => {
    try {
      const newValues = { ...data };
      if (!isAvailableVendor) {
        newValues.vendor = user?.id ?? 4;
      }
      await onSave(newValues as any);
      reset();
    } catch (error) {
      console.error(error);
    }
  };
  useEffect(() => {
    if (isGrid) {
      reset({ ...defaultValues, items: [] } as unknown as any);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGrid]);
  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      <Grid container>
        <Grid item xs={12} md={12}>
          <Stack spacing={3}>
            <Card sx={{ p: 3 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={isAvailableVendor ? 6 : 12}>
                  <RHFTextField
                    name="table"
                    label={isFoodCompany ? translates.table : 'Cliente'}
                    placeholder={isFoodCompany ? 'Mesa 01' : 'Nombre del cliente'}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                {isAvailableVendor ? (
                  <Grid item xs={12} sm={6}>
                    <RHFSelect
                      name="vendor"
                      size="small"
                      label={isFoodCompany ? translates.waiter : 'Vendedor'}
                      fullWidth
                      disabled={isEdit}
                      sx={{ height: '100%' }}
                      InputProps={{ style: { height: '100%' } }}
                      InputLabelProps={{ shrink: true }}
                    >
                      {dataUsers?.map?.((service: any) => (
                        <MenuItem key={service.id + service.name} value={service.id}>
                          {service.name}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Grid>
                ) : (
                  <div />
                )}
              </Grid>
            </Card>
            <Box>
              <Grid container>
                <Grid item xs={12} md={12}>
                  <Stack spacing={3}>
                    {isCustomOrder ? (
                      <ProductItemsGridCustomOrder
                        isEdit={isEdit}
                        defaultValuesItems={defaultValues.items}
                      />
                    ) : isGrid ? (
                      <ProductItemsGridOrder isEdit={isEdit} />
                    ) : (
                      <ProductItemsListOrder
                        isEdit={isEdit}
                        defaultValuesItems={defaultValues.items}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </Box>
            <LoadingButton
              disabled={!isValid}
              type="submit"
              variant="outlined"
              size="large"
              loading={isLoading}
            >
              {!isEdit ? [`${translates.create} ${translates.order}`] : [translates.saveChanges]}
            </LoadingButton>
          </Stack>
        </Grid>
      </Grid>
    </FormProvider>
  );
}
